import React from 'react';
import {
  BooleanInput,
  SaveButton,
  SaveContextProvider,
  SelectInput,
  SimpleForm,
  Toolbar,
} from 'react-admin';
import { Box, Button, Dialog, DialogTitle, Typography } from '@mui/material';
import LocationInput from 'components/LocationInput';
import PositionInput from 'components/PositionInput';
import SpecialtyInput from 'components/SpecialtyInput';
import useApi from 'hooks/useApi';
import {
  buildPoolCreatePayload,
  getPoolCreateErrorMessage,
  validatePoolCreateValues,
} from './poolCreateEditModal.utils';

type PoolCreateEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialLocation?: number;
};

const shiftNeedsChoices = [
  { name: '1-5', id: 'needs_1_5' },
  { name: '6-10', id: 'needs_6_10' },
  { name: '11-20', id: 'needs_11_20' },
  { name: '20+', id: 'needs_20_plus' },
];

const poolStatusChoices = [
  { id: 'active', name: 'Active' },
  { id: 'deleted', name: 'Deleted' },
];

const PoolCreateEditModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialLocation,
}: PoolCreateEditModalProps) => {
  const api = useApi();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    setErrorMessage('');
    onClose();
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      await api({
        url: '/v3/admin/pools',
        method: 'POST',
        data: buildPoolCreatePayload(values),
      });

      onSuccess();
    } catch (error: any) {
      setErrorMessage(getPoolCreateErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Pool</DialogTitle>
      <SaveContextProvider
        value={{ save: handleSubmit, saving: isLoading, mutationMode: 'pessimistic' }}
      >
        <SimpleForm
          record={{ location_id: initialLocation }}
          validate={validatePoolCreateValues}
          toolbar={
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <SaveButton label="Create Pool" alwaysEnable />
              <Button onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
            </Toolbar>
          }
        >
          <LocationInput disabled={Boolean(initialLocation)} />
          <PositionInput multiple source="position_ids" />
          <SpecialtyInput multiple positionSource="position_ids" />
          <SelectInput source="status" choices={poolStatusChoices} emptyText="None" />
          <SelectInput source="shift_needs" choices={shiftNeedsChoices} emptyText="None" />
          <BooleanInput source="pause_auto_updates" />
          <BooleanInput source="requires_pro_review" />
          {errorMessage && (
            <Box mt={1}>
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            </Box>
          )}
        </SimpleForm>
      </SaveContextProvider>
    </Dialog>
  );
};

export default PoolCreateEditModal;
